import React, { useMemo, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Box, Avatar, Chip, CircularProgress, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    PhoneOutlined,
    LockOutlined,
    EmailOutlined,
} from '@mui/icons-material';
import MDTypography from 'components/MDTypography';

/* -------------------------------------------------------------------------- */
/* Config                                                                     */
/* -------------------------------------------------------------------------- */

const formatCardDate = (value) => {
    if (!hasValue(value)) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const STATUS_CONFIG = {
    New: { accent: '#3b82f6', tint: 'rgb(240, 245, 255)' },
    'In Progress': { accent: '#f59e0b', tint: 'rgb(255, 247, 240)' },
    Qualified: { accent: '#10b981', tint: 'rgb(240, 255, 245)' },
    Disqualified: { accent: '#ef4444', tint: 'rgb(255, 240, 240)' },
};

const STATUSES = Object.keys(STATUS_CONFIG);
const LOCKED_STATUSES = ['Qualified', 'Disqualified'];
const AVATAR_COLORS = ['#6366f1', '#0ea5e9', '#14b8a6', '#f97316', '#ec4899', '#8b5cf6'];

const INK = '#1e293b';
const MUTED = '#64748b';
const FAINT = '#94a3b8';
const BORDER = 'rgba(15, 23, 42, 0.07)';

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

// null, undefined, "" and "   " all count as "no value"
const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== '';

const getInitials = (lead) => {
  const first = (lead.first_name || '').trim();
  const last = (lead.last_name || '').trim();
  if (first || last) return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  const parts = String(lead.name || lead.email || '?').trim().split(/\s+/);
  return `${parts[0]?.charAt(0) || '?'}${parts[1]?.charAt(0) || ''}`.toUpperCase();
};

const avatarColor = (id) => AVATAR_COLORS[Math.abs(Number(id) || 0) % AVATAR_COLORS.length];

/* -------------------------------------------------------------------------- */
/* Small building blocks                                                      */
/* -------------------------------------------------------------------------- */

const DetailLine = ({ text }) => {
  if (!hasValue(text)) return null; // nothing to show -> nothing rendered
  return (
      <MDTypography
          variant="caption"
          title={text}
          sx={{
            display: 'block',
            fontSize: '0.78rem',
            lineHeight: 1.4,
            color: MUTED,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
      >
        {text}
      </MDTypography>
  );
};

const scrollbar = (size) => ({
  '&::-webkit-scrollbar': { width: size, height: size },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(100, 116, 139, 0.28)',
    borderRadius: size,
    '&:hover': { background: 'rgba(100, 116, 139, 0.5)' },
  },
});

/* -------------------------------------------------------------------------- */
/* Card                                                                       */
/* -------------------------------------------------------------------------- */

const LeadCardContent = ({ lead, accent, locked, isDragging, isUpdating, onClick }) => {
  const phone = hasValue(lead.phone) ? lead.phone : lead.mobile;
  const hasName = hasValue(lead.name);
  const title = hasName ? lead.name : lead.email || lead.company || `Lead ${lead.id}`;
  const owner = hasValue(lead.assigned_to_name) ? lead.assigned_to_name : 'Unassigned';
  const updatedOn = formatCardDate(lead.last_activity_raw);

  return (
      <Box
          onClick={onClick}
          sx={{
            position: 'relative',
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '14px',
            border: '1px solid',
            borderColor: isDragging ? alpha(accent, 0.55) : BORDER,
            boxShadow: isDragging
                ? '0 14px 30px rgba(15, 23, 42, 0.2)'
                : '0 1px 2px rgba(15, 23, 42, 0.04)',
            // The tilt lives here, on the inner element, so it never fights with
            // the transform react-beautiful-dnd puts on the outer wrapper.
            transform: isDragging ? 'rotate(1.5deg)' : 'none',
            transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
            cursor: locked ? 'pointer' : isDragging ? 'grabbing' : 'grab',
            overflow: 'hidden',
            '&:hover': isDragging
                ? {}
                : {
                  boxShadow: '0 4px 14px rgba(15, 23, 42, 0.09)',
                  borderColor: alpha(accent, 0.4),
                },
          }}
      >
        {isUpdating && (
            <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.72)',
                  backdropFilter: 'blur(1px)',
                }}
            >
              <CircularProgress size={22} />
            </Box>
        )}

        {/* Header: avatar, name, lock */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <Avatar
              sx={{
                width: 34,
                height: 34,
                fontSize: '0.8rem',
                fontWeight: 600,
                bgcolor: alpha(avatarColor(lead.id), 0.14),
                color: avatarColor(lead.id),
                flexShrink: 0,
              }}
          >
            {getInitials(lead)}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <MDTypography
                variant="body2"
                fontWeight="medium"
                title={hasName ? lead.name : 'This lead has no name yet'}
                sx={{
                  fontSize: '0.9rem',
                  lineHeight: 1.35,
                  color: INK,
                  fontStyle: hasName ? 'normal' : 'italic',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
            >
              {title}
            </MDTypography>

          </Box>

          {locked && (
              <Tooltip title={`${lead.status} leads can't be moved`} placement="top">
                <LockOutlined sx={{ fontSize: 16, color: FAINT, flexShrink: 0, mt: '2px' }} />
              </Tooltip>
          )}
        </Box>

          {/* Email: icon + address, only when there is a value */}
          {hasValue(lead.email) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, minWidth: 0 }}>
                  <EmailOutlined sx={{ fontSize: 16, color: FAINT, flexShrink: 0 }} />
                  <MDTypography
                      variant="body2"
                      title={lead.email}
                      sx={{
                          fontSize: '0.8125rem',
                          lineHeight: 1.4,
                          color: MUTED,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                      }}
                  >
                      {lead.email}
                  </MDTypography>
              </Box>
          )}

          {/* Phone: icon + number, only when there is a value */}
          {hasValue(phone) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: hasValue(lead.email) ? 0.75 : 1.5, minWidth: 0 }}>
                  <PhoneOutlined sx={{ fontSize: 16, color: FAINT, flexShrink: 0 }} />
                  <MDTypography
                      variant="body2"
                      title={phone}
                      sx={{
                          fontSize: '0.8125rem',
                          lineHeight: 1.4,
                          color: MUTED,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                      }}
                  >
                      {phone}
                  </MDTypography>
              </Box>
          )}

          {/* Footer: ID + date on the left, owner avatar on the right, source chip below */}
          <Box sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${BORDER}` }}>
              <Box
                  sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      minHeight: 26,
                  }}
              >
                  {/* ID · date */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      <MDTypography
                          variant="caption"
                          fontWeight="medium"
                          sx={{
                              color: MUTED,
                              fontSize: '0.72rem',
                              lineHeight: 1,
                              letterSpacing: '0.02em',
                              flexShrink: 0,
                              display: 'inline-flex',
                              alignItems: 'center',
                          }}
                      >
                          #{lead.id}
                      </MDTypography>

                      {updatedOn && (
                          <>
                              <Box
                                  component="span"
                                  sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: FAINT, flexShrink: 0 }}
                              />
                              <Tooltip title={`Updated at ${lead.last_activity || updatedOn}`} placement="top" arrow>
                                  <Box
                                      component="span"
                                      sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'default' }}
                                  >
                                      <MDTypography
                                          variant="caption"
                                          sx={{
                                              color: MUTED,
                                              fontSize: '0.72rem',
                                              lineHeight: 1,
                                              whiteSpace: 'nowrap',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                          }}
                                      >
                                          {updatedOn}
                                      </MDTypography>
                                  </Box>
                              </Tooltip>
                          </>
                      )}
                  </Box>

                  {/* Owner/Assigned to avatar */}
                  <Tooltip title={owner === 'Unassigned' ? 'Unassigned' : `Assigned to ${owner}`} placement="top" arrow>                      <Avatar
                          src={lead.assigned_to_avatar || undefined}
                          alt={owner}
                          sx={{
                              width: 26,
                              height: 26,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              bgcolor: owner === 'Unassigned' ? '#e2e8f0' : alpha(avatarColor(lead.assigned_to), 0.16),
                              color: owner === 'Unassigned' ? MUTED : avatarColor(lead.assigned_to),
                              flexShrink: 0,
                              ml: 'auto',
                          }}
                      >
                          {owner.charAt(0).toUpperCase()}
                      </Avatar>
                  </Tooltip>
              </Box>

              {/* Source chip: own row, only when there is a source */}
              {hasValue(lead.source) && (
                  <Chip
                      label={lead.source}
                      size="small"
                      sx={{
                          mt: 0.75,
                          height: 22,
                          maxWidth: '100%',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          color: '#2563eb',
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          '& .MuiChip-label': { px: 1, overflow: 'hidden', textOverflow: 'ellipsis' },
                      }}
                  />
              )}
          </Box>
      </Box>
  );
};

/* -------------------------------------------------------------------------- */
/* Board                                                                      */
/* -------------------------------------------------------------------------- */

const LeadsKanbanBoard = ({ leads, onDragEnd, onLeadClick, updatingLeadId }) => {
  // Group once per leads change instead of filtering four times per render
  const columns = useMemo(() => {
    const grouped = STATUSES.reduce((acc, s) => ({ ...acc, [s]: [] }), {});
    (leads || []).forEach((lead) => {
      const status = grouped[lead?.status] ? lead.status : 'New';
      grouped[status].push({ ...lead, lead_score: Number(lead.lead_score || 0) });
    });
    return grouped;
  }, [leads]);

  // Dropping back into the same column is a no-op, so skip the API call.
  const handleDragEnd = useCallback(
      (result) => {
        const { source, destination } = result;
        if (!destination || source.droppableId === destination.droppableId) return;
        onDragEnd?.(result);
      },
      [onDragEnd]
  );

  return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 2,
              p: 2,
              height: 'calc(100vh - 320px)',
              minHeight: 440,
              overflowX: 'auto',
              overflowY: 'hidden',
              ...scrollbar('8px'),
            }}
        >
          {STATUSES.map((status) => {
            const { accent, tint } = STATUS_CONFIG[status];
            const statusLeads = columns[status];

            return (
                <Box
                    key={status}
                    sx={{
                      flex: '1 1 280px',
                      minWidth: 280,
                      maxWidth: 420,
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                      backgroundColor: tint,
                      borderRadius: '14px',
                      border: `1px solid ${BORDER}`,
                      overflow: 'hidden',
                    }}
                >
                  {/* Column header (outside the droppable, so no sticky hacks needed) */}
                  <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1.5,
                        flexShrink: 0,
                      }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: accent }} />
                      <MDTypography variant="h6" sx={{ fontSize: '0.9rem', color: INK }}>
                        {status}
                      </MDTypography>
                    </Box>
                    <MDTypography
                        variant="caption"
                        fontWeight="medium"
                        sx={{
                          minWidth: 24,
                          textAlign: 'center',
                          px: 1,
                          py: '2px',
                          borderRadius: '10px',
                          color: accent,
                          backgroundColor: alpha(accent, 0.12),
                        }}
                    >
                      {statusLeads.length}
                    </MDTypography>
                  </Box>

                  {/* Droppable body: always tall enough to accept a drop, even when empty */}
                  <Droppable droppableId={status}>
                    {(dropProvided, dropSnapshot) => (
                        <Box
                            ref={dropProvided.innerRef}
                            {...dropProvided.droppableProps}
                            sx={{
                              flex: 1,
                              minHeight: 120,
                              overflowY: 'auto',
                              px: 1.25,
                              pb: 1,
                              pt: 0.5,
                              transition: 'background-color 0.2s ease',
                              backgroundColor: dropSnapshot.isDraggingOver ? alpha(accent, 0.08) : 'transparent',
                              ...scrollbar('6px'),
                            }}
                        >
                          {statusLeads.length === 0 && !dropSnapshot.isDraggingOver && (
                              <Box
                                  sx={{
                                    mt: 1,
                                    py: 4,
                                    textAlign: 'center',
                                    borderRadius: '10px',
                                    border: '1px dashed rgba(100, 116, 139, 0.3)',
                                  }}
                              >
                                <MDTypography variant="caption" sx={{ color: FAINT }}>
                                  No leads here
                                </MDTypography>
                              </Box>
                          )}

                          {statusLeads.map((lead, index) => {
                            const locked = LOCKED_STATUSES.includes(lead.status);
                            const isUpdating = updatingLeadId === lead.id;

                            return (
                                <Draggable
                                    key={lead.id}
                                    draggableId={String(lead.id)}
                                    index={index}
                                    isDragDisabled={locked || isUpdating}
                                >
                                  {(dragProvided, dragSnapshot) => (
                                      // Keep this wrapper plain: dnd owns its transform/transition.
                                      <div
                                          ref={dragProvided.innerRef}
                                          {...dragProvided.draggableProps}
                                          {...dragProvided.dragHandleProps}
                                          style={{ ...dragProvided.draggableProps.style, marginBottom: 10 }}
                                      >
                                        <LeadCardContent
                                            lead={lead}
                                            accent={accent}
                                            locked={locked}
                                            isDragging={dragSnapshot.isDragging}
                                            isUpdating={isUpdating}
                                            onClick={() => onLeadClick?.(lead)}
                                        />
                                      </div>
                                  )}
                                </Draggable>
                            );
                          })}
                          {dropProvided.placeholder}
                        </Box>
                    )}
                  </Droppable>
                </Box>
            );
          })}
        </Box>
      </DragDropContext>
  );
};

export default LeadsKanbanBoard;